import { PERIOD_LABEL, PERIODS, type Period } from '@qlk/shared';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  kyNgoaiMocDuLieu,
  kyTuMa,
  laMaKy,
  MA_KY_MAC_DINH,
  type KyTrang,
  type MocChonKy,
} from '../lib/kyTrang.js';
import { useMocSoLieu } from '../modules/analytics/api.js';

/**
 * Ô chọn thời gian **dùng chung** của thanh trên.
 *
 * Trước đây mỗi trang tự dựng bộ chọn kỳ riêng, nên cùng một câu hỏi ("đang xem
 * tháng nào?") có bốn chỗ trả lời khác nhau và không chỗ nào chia sẻ được bằng
 * link. Nay một ô duy nhất, giá trị **nằm trên URL** (`?ky=`) nên dán link là
 * người khác thấy đúng kỳ đó.
 *
 * Mỗi trang khai **kiểu kỳ** nó dùng trong `KIEU_KY_THEO_DUONG_DAN`:
 *
 * - `moc` — kỳ theo **mốc dữ liệu**, tám dạng như YouTube Studio (7/28/90/365
 *   ngày, toàn thời gian, một năm, một tháng, khoảng tự chọn). Dùng cho số
 *   liệu YouTube; xem `lib/kyTrang.ts` và `@qlk/shared/ky.ts`.
 * - `ngay` — bản **cũ**: chỉ 5 preset, không mốc dữ liệu. Không trang nào còn
 *   dùng; giữ kiểu này để `?ky=28d` của link cũ vẫn hợp lệ ở mọi chỗ.
 * - `thang` — kỳ kế toán: một tháng **hoặc một quý**. Dùng cho tiền.
 * - `thang-don` — chỉ tháng, không quý. Dùng cho trang mà API chỉ nhận
 *   `YYYY-MM`: cho chọn quý ở đó là dựng một ô bấm vào ra lỗi 400.
 *
 * Trang không khai thì thanh trên **ẩn** ô chọn: thà không có ô còn hơn có một
 * ô bấm vào không đổi gì.
 */

export const KIEU_KY = ['moc', 'ngay', 'thang', 'thang-don'] as const;
export type KieuKy = (typeof KIEU_KY)[number];

/**
 * Giá trị hợp lệ của `?ky=` với kiểu `ngay` — **đúng bằng** `PERIODS` của
 * module 01.
 *
 * Trước chỉ có 7/28/90 ngày, nhưng trang Kênh vốn có thêm "365 ngày" và "toàn
 * thời gian"; nối nó vào ô chung mà bỏ hai kỳ đó là lấy mất số liệu người dùng
 * đang dùng. Trang Tổng quan nhận cùng danh sách (`DASHBOARD_RANGES`).
 */
export const KY_NGAY = PERIODS;
export type KyNgay = Period;

export const NHAN_KY_NGAY: Record<KyNgay, string> = PERIOD_LABEL;

export const MAC_DINH_KY_NGAY: KyNgay = '28d';

const KHOA_THANG = /^\d{4}-(0[1-9]|1[0-2])$/;
const KHOA_QUY = /^\d{4}-Q[1-4]$/;

/**
 * Kiểu kỳ của từng đường dẫn.
 *
 * Khai ở đây chứ không trong `module.ts` của từng module: ô chọn nằm ở thanh
 * trên — thứ của khung ứng dụng — và nó phải biết câu trả lời **trước** khi
 * trang kịp render.
 */
export const KIEU_KY_THEO_DUONG_DAN: { duongDan: string; kieu: KieuKy }[] = [
  /*
   * Bốn trang số liệu YouTube dùng **cùng một** kỳ và giữ nó khi chuyển qua
   * lại (xem `KyChungProvider`), giống cách YouTube Studio nhớ kỳ đang xem.
   */
  { duongDan: '/', kieu: 'moc' },
  { duongDan: '/kenh', kieu: 'moc' },
  { duongDan: '/network', kieu: 'moc' },
  { duongDan: '/phan-tich', kieu: 'moc' },
  { duongDan: '/doi-soat-nhan-su', kieu: 'thang' },
  /*
   * Hai trang dưới dùng `thang-don`: `GET /revenue/tong-quan` và
   * `GET /team/thu-nhap-cua-toi` chỉ nhận kỳ `YYYY-MM`, không có đường nào gộp
   * quý. Bày quý ở đó là mời người dùng bấm vào một lựa chọn luôn lỗi.
   */
  { duongDan: '/doanh-thu', kieu: 'thang-don' },
  { duongDan: '/thu-nhap-cua-toi', kieu: 'thang-don' },
];

/** Kiểu kỳ của một đường dẫn, `null` khi trang không dùng ô chọn chung. */
export function kieuKyCuaDuongDan(duongDan: string): KieuKy | null {
  /* Khớp **chính xác** chứ không theo tiền tố: `/kenh/:id` là trang chi tiết,
     nó có kỳ riêng của từng tab, không dùng ô chung. */
  return KIEU_KY_THEO_DUONG_DAN.find((x) => x.duongDan === duongDan)?.kieu ?? null;
}

/** Kỳ tháng hiện tại theo giờ máy người dùng, dạng `YYYY-MM`. */
export function thangHienTai(homNay = new Date()): string {
  return `${String(homNay.getFullYear())}-${String(homNay.getMonth() + 1).padStart(2, '0')}`;
}

/** `2026-11` → `2026-Q4`. */
export function quyCuaThangCuaLich(thang: string): string {
  const [nam, th] = thang.split('-');
  const q = Math.floor((Number(th) - 1) / 3) + 1;
  return `${nam ?? '?'}-Q${String(q)}`;
}

export interface KyChung {
  kieu: KieuKy | null;
  /** Giá trị thô trên URL: `28d`, `2026-10`, `2026-Q4` hoặc `tu-…_den-…`. */
  giaTri: string;
  /** Đặt giá trị mới — ghi vào `?ky=` nên nút Lùi của trình duyệt vẫn đúng. */
  dat: (giaTri: string) => void;
  /** `true` khi giá trị là một quý (`YYYY-Qn`). */
  laQuy: boolean;
  /**
   * Kỳ **đã giải** (khoảng ngày, kỳ trước, nhãn) — chỉ có với kiểu `moc`.
   * Trang kiểu khác đọc `giaTri`.
   */
  ky: KyTrang | null;
  /** Hai đầu trục thời gian có số liệu + các năm có số liệu; rỗng khi chưa tải xong. */
  moc: MocChonKy;
  /**
   * Kỳ đang xem nằm **ngoài** khoảng có số liệu ⇒ không có số nào để hiện.
   *
   * Trang truyền tiếp vào `StatStrip kyRong` để mọi ô hiện `—`: API trả 0 cho
   * những kỳ đó, mà `0,00 $` đọc ra là "tụt về không". Dòng chữ giải thích nằm
   * cạnh ô chọn kỳ (`ui/ChonKy`).
   */
  kyRong: boolean;
  /**
   * Thu mốc dữ liệu về **một kênh**, hoặc `null` để trở lại toàn phạm vi.
   *
   * Trang Hiệu suất có ô "Phạm vi" riêng (tất cả / một nhóm / một kênh) nằm
   * trong thân trang, còn ô chọn kỳ nằm ở thanh trên. Khi phạm vi là một kênh
   * thì nhóm "Theo năm" phải là năm của **kênh đó**, nên trang báo xuống đây
   * thay vì dựng một mốc thứ hai — hai mốc là hai câu trả lời lệch nhau.
   */
  datKenhMoc: (channelId: string | null) => void;
}

/**
 * Kỳ đang xem, nhớ giữa các trang.
 *
 * YouTube Studio giữ nguyên kỳ khi bạn đi từ Tổng quan sang Nội dung sang
 * Phân tích; ở đây cũng vậy — nhưng kỳ phải **nằm trên URL** để dán link được,
 * nên mở một trang không có `?ky=` thì provider ghi lại mã đã nhớ (`replace`,
 * không sinh thêm một bước lịch sử).
 *
 * `sessionStorage` chứ không `localStorage`: nhớ trong phiên làm việc là đủ,
 * còn mở app sáng hôm sau mà vẫn là "tháng 9 năm ngoái" thì khó hiểu hơn là
 * tiện.
 */
const KHOA_NHO_KY = 'qlk.ky';

function docKyDaNho(): string {
  try {
    const x = sessionStorage.getItem(KHOA_NHO_KY) ?? '';
    return laMaKy(x) ? x : '';
  } catch {
    /* Trình duyệt chặn storage (chế độ riêng tư): coi như chưa nhớ gì. */
    return '';
  }
}

function nhoKy(ma: string): void {
  try {
    sessionStorage.setItem(KHOA_NHO_KY, ma);
  } catch {
    /* Không nhớ được thì thôi — kỳ vẫn nằm trên URL. */
  }
}

const BoiCanh = createContext<KyChung | null>(null);

export interface KyChungProviderProps {
  kieu: KieuKy | null;
  children: ReactNode;
}

export function KyChungProvider({ kieu, children }: KyChungProviderProps) {
  const [thamSo, datThamSo] = useSearchParams();

  /*
   * Mốc dữ liệu chỉ cần cho kiểu `moc`. Mọi vai trò mở được bốn trang đó đều
   * có `analytics:view` (ma trận ở `core/danhMucVaiTro.ts`), nên endpoint này
   * không dựng thêm cổng nào.
   */
  const [kenhMoc, datKenhMoc] = useState<string | null>(null);
  const mocTruyVan = useMocSoLieu(kieu === 'moc', kenhMoc ?? undefined);
  const cacNam = mocTruyVan.data?.cacNam;
  const moc = useMemo<MocChonKy>(
    () => ({
      ngayDau: mocTruyVan.data?.ngaySomNhatCoSoLieu ?? '',
      ngayCuoi: mocTruyVan.data?.ngayCuoiCoSoLieu ?? '',
      cacNam: cacNam ?? [],
    }),
    [mocTruyVan.data?.ngaySomNhatCoSoLieu, mocTruyVan.data?.ngayCuoiCoSoLieu, cacNam],
  );

  /** Kỳ của trang vừa xem, đọc một lần cho mỗi lượt render. */
  const daNho = kieu === 'moc' ? docKyDaNho() : '';

  const giaTri = useMemo(() => {
    const tho = thamSo.get('ky') ?? '';

    if (kieu === 'moc') {
      if (laMaKy(tho)) return tho;
      /* Mở trang không có `?ky=`: dùng lại kỳ của trang vừa xem. */
      return daNho || MA_KY_MAC_DINH;
    }

    if (kieu === 'thang' || kieu === 'thang-don') {
      if (KHOA_THANG.test(tho)) return tho;
      /* Quý chỉ hợp lệ với `thang`. Link `?ky=2026-Q4` dán sang trang
         `thang-don` thì về tháng này, không gửi quý cho API không hiểu nó. */
      if (kieu === 'thang' && KHOA_QUY.test(tho)) return tho;
      return thangHienTai();
    }

    /* Kiểu `ngay`, hoặc trang không dùng ô chung: giá trị lạ thì về mặc định. */
    return (KY_NGAY as readonly string[]).includes(tho) ? tho : MAC_DINH_KY_NGAY;
  }, [thamSo, kieu, daNho]);

  /*
   * Ghi kỳ **đã nhớ** vào URL để dán link được. `replace`: không phải một bước
   * điều hướng của người dùng.
   *
   * ⛔ Chỉ ghi khi URL **thiếu** `?ky=` và thật sự có kỳ đã nhớ. Ghi mọi lúc
   * giá trị lệch nhau sẽ đè lên cả những trang đang **tự chuyển hướng** đi chỗ
   * khác: cộng tác viên mở `/` phải bị đẩy sang `/san-xuat`, nhưng hiệu ứng
   * này ghi `?ky=28d` lên `/` và giữ họ ở lại (bài `e2e/05`).
   */
  const thoKy = thamSo.get('ky') ?? '';
  useEffect(() => {
    if (kieu !== 'moc' || thoKy !== '' || daNho === '') return;
    const tiep = new URLSearchParams(thamSo);
    tiep.set('ky', daNho);
    datThamSo(tiep, { replace: true });
  }, [kieu, thoKy, daNho, thamSo, datThamSo]);

  const ky = useMemo(() => (kieu === 'moc' ? kyTuMa(giaTri, moc) : null), [kieu, giaTri, moc]);

  const giaTriChung = useMemo<KyChung>(
    () => ({
      kieu,
      giaTri,
      ky,
      moc,
      datKenhMoc,
      kyRong: ky !== null && kyNgoaiMocDuLieu(ky, moc),
      laQuy: KHOA_QUY.test(giaTri),
      dat: (moi) => {
        if (kieu === 'moc') nhoKy(moi);
        const tiep = new URLSearchParams(thamSo);
        tiep.set('ky', moi);
        /*
         * `replace: false` — đổi kỳ là một bước điều hướng thật: bấm Lùi phải
         * về kỳ trước đó, không nhảy khỏi trang.
         */
        datThamSo(tiep);
      },
    }),
    [kieu, giaTri, ky, moc, thamSo, datThamSo],
  );

  return <BoiCanh.Provider value={giaTriChung}>{children}</BoiCanh.Provider>;
}

/**
 * Kỳ đang chọn ở thanh trên.
 *
 * Trang nào đã khai trong `KIEU_KY_THEO_DUONG_DAN` thì dùng hook này thay cho
 * bộ chọn riêng của mình (chuyển dần ở bước 2–3).
 */
export function useKyChung(): KyChung {
  const ctx = useContext(BoiCanh);
  if (!ctx) {
    throw new Error('useKyChung phải nằm trong <KyChungProvider> (xem layout/AppShell)');
  }
  return ctx;
}
